import { useMemo } from "react";
import { useGetOrdersInfiniteQuery } from "app/api/orders";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectSearch, selectFilters, selectSort, setSearch, setFilters, OrderFilters, setSort, SortBy } from "pages/orders/ordersSlice";
import { useDebounce } from "../../hooks/useDebounce";
import { SortDirection } from "components/DataTable";

type OrderSearchOptions = {
  skipEmptySearch?: boolean;
};

export const useOrderSearch = ({ skipEmptySearch }: OrderSearchOptions = {}) => {
  const dispatch = useAppDispatch();

  const search = useAppSelector(selectSearch);
  const filters = useAppSelector(selectFilters);
  const sort = useAppSelector(selectSort);
  const debouncedSearch = useDebounce(search, { delayMs: 500 });

  const activeFiltersCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const { data, fetchNextPage, isFetching, isLoading } = useGetOrdersInfiniteQuery({
    filterText: debouncedSearch,
    sortBy: sort.sortBy,
    sortDir: sort.sortDir,
    ...filters,
  });

  const orders = useMemo(() => data?.pages.flat() || [], [data]);

  const updateSearch = (search: string) => {
    dispatch(setSearch(search));
  };
  const updateFilters = (filters: OrderFilters) => {
    dispatch(setFilters(filters));
  };
  const updateSort = (sortBy: SortBy, sortDir: SortDirection) => {
    dispatch(setSort({ sortBy, sortDir }));
  };

  return {
    search,
    filters,
    sort,
    activeFiltersCount,
    orders: skipEmptySearch && !debouncedSearch ? [] : orders,
    isFetching,
    isLoading,
    setSearch: updateSearch,
    setFilters: updateFilters,
    setSort: updateSort,
    fetchNextPage,
  };
};
